<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';
import { computed } from 'vue';
import IconAnalysNav220 from '@/components/svg/IconAnalysNav220.vue';
import { calculateDDay } from '@/utils/dateFormatter';

const analysisStore = useAnalysisStore();

const props = defineProps({
  guestUserId: {
    required: true,
  },
  yongsinTicketData: {
    required: true,
  },
  yongsinLogData: {
    required: true,
  },
  hasPermission: {
    required: false,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const yongsinLogData = computed(() => {
  if (!props.yongsinLogData?.length) return null;
  const yongsinData = props.yongsinLogData[0];
  return yongsinData;
});

const dDay = computed(() => calculateDDay(yongsinLogData.value?.expiryDate, false));

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formattedCreatedAt = computed(() => formatDate(yongsinLogData.value?.createdAt));
const formattedExpiryDate = computed(() => formatDate(yongsinLogData.value?.expiryDate));

const goProduct = () => {
  router.push('/purchase/tickets');
};

const goPopAnalysis = () => {
  // 상세 흐름의 '용신분석 시작하기' 발화 추가 (안내서 보기 버튼과 화면 조건 배타적이라 중복 없음)
  // 신규 type — DB 등록 후 작동
  addContentLogAPI({
    contentType: props.hasPermission ? 'ay_yongsin_btn_start_paid' : 'ay_yongsin_btn_start_free',
  });
  emit('goPopAnalysis');
};

const clickGoYongsinButton = () => {
  addContentLogAPI({
    contentType: props.hasPermission ? 'ay_yongsin_btn_guide_paid' : 'ay_yongsin_btn_guide_free',
  });
  analysisStore.navigateToContent('/youngsin', 0);
};
</script>
<template>
  <div class="overview-payment-info-wrap st-youngsin">
    <div class="overview-payment-inner">
      <div class="overview-none-wrap" v-if="!hasPermission">
        <div class="overview-none-title">
          <i class="ticket-icon__analysis"></i>
          <span class="tit">보유 용신분석권</span>
          <span class="cnt">{{ yongsinTicketData?.totalCount || 0 }} 건</span>
        </div>
      </div>

      <!-- 분석권으로 이용 중일 때 -->
      <div class="overview-payment-info-content-box" v-else>
        <div class="overview-payment-info-content">
          <div class="overview-payment-info-title">낭만 용신분석을 이용하고 있어요!</div>
          <div class="overview-payment-info-date-box">
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">시작일</div>
              <div class="overview-payment-info-date-value">{{ formattedCreatedAt || '-' }}</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">만료일</div>
              <div class="overview-payment-info-date-value" v-if="yongsinLogData?.expiryDate">
                {{ formattedExpiryDate }}
              </div>
              <div class="overview-payment-info-date-value" v-else>-</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">남은 기간</div>
              <div class="overview-payment-info-date-value red" v-if="yongsinLogData?.expiryDate">
                {{ dDay }} 일 남음
              </div>
              <div class="overview-payment-info-date-value red" v-else>소장용</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 용신분석 소개 -->
      <div class="overview-pay-info-box">
        <div class="tit-box"><IconAnalysNav220 /> <span>낭만 용신분석은?</span></div>
        <div class="txt-box">
          <div class="txt">
            낭만만세력의 "용신분석”은<br />
            사주를 깊이 공부하거나 실제 상담과 연구를<br />
            이어가는 분들을 위해 여러 각도에서 참고할 수 있는 <br />
            보조 자료를 제공해드리고자 준비된 서비스입니다.
          </div>
          <div class="txt">
            이 서비스는 <span class="txt-bold">용신 해석의 다양한 기준과 논리</span>를 토대로<br />
            하나의 결과가 아닌 다각적 근거를 한눈에 있도록 구성하고 있으며 이를 위해 세가지 과정을
            거쳐 분석 추론을 제시하고 있습니다.
          </div>
        </div>
      </div>
    </div>

    <div class="overview_guide_button_box">
      <button
        v-if="analysisStore.selectedMenu === '/overviewYongsin'"
        class="overview_guide_button"
        @click="clickGoYongsinButton"
      >
        용신분석 안내서 보기
      </button>
      <div
        v-if="
          analysisStore.selectedMenu.includes('/youngsin') &&
          !props.hasPermission
        "
        class="overview_guide_button_start"
        @click="goPopAnalysis"
      >
        용신분석 시작하기
      </div>
      <!-- <div class="overview-payment-info-btn-line" @click="goProduct">상품 소개 보러 가기</div> -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
.overview-payment-info-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  padding: 28px;
  gap: 24px;
  overflow-y: scroll;
}

.st-youngsin {
  .overview-none-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    .overview-none-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 20px 28px;
      border-radius: 12px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      .tit {
        color: var(--Gray-800, #333);
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 160% */
      }
      .cnt {
        color: var(--orange-300-main, #eb4c10);
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 171.429% */
        margin-left: auto;
      }
    }
  }

  .overview-pay-info-box {
    display: flex;
    padding: 28px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    border-radius: 12px;
    background: rgba(78, 128, 214, 0.05);
    box-sizing: border-box;
    width: 100%;
    .tit-box {
      color: #4e80d6;
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px; /* 155.556% */

      display: flex;
      align-items: center;
      gap: 8px;

      span {
        display: inline-flex;
        align-items: center;
        line-height: inherit;
      }
    }
    .txt-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      color: var(--Gray-700, #575757);
      font-size: 15px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px; /* 160% */

      .txt-bold {
        color: #4e80d6;
        font-weight: 700;
      }
    }
  }

  .overview-payment-info-content-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 20px;
  }
  .overview-payment-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 28px;
  }
  .overview-payment-info-content {
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 20px 28px;

    .overview-payment-info-title {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
      margin-bottom: 8px;
    }

    .overview-payment-info-date-box {
      display: flex;
      width: 100%;
      justify-content: flex-start;
      align-items: flex-start;
      flex-direction: column;
    }
    .overview-payment-info-date {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: flex-start;
      .overview-payment-info-date-title {
        color: var(--Gray-800, #333);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 171.429% */
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .overview-payment-info-date-value {
        color: var(--Gray-800, #333);
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 171.429% */
        &.red {
          color: var(--orange-300-main, #eb4c10);
        }
      }
    }
  }
  .overview_guide_button_box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 28px;
    margin-top: auto;
  }
  .overview_guide_button {
    display: flex;
    padding: 14px 20px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 6px;
    border: 1px solid var(--orange-300-main, #eb4c10);
    background: var(--gray-100-white, #fff);
    color: var(--orange-300-main, #eb4c10);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }
  .overview_guide_button_start {
    cursor: pointer;
    display: flex;
    width: 100%;
    padding: 14px 20px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 6px;
    background: var(--orange-300-main, #eb4c10);
    color: var(--gray-100-white, #fff);
    text-align: center;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px; /* 133.333% */
  }
  .overview-payment-info-btn-line {
    cursor: pointer;
    color: var(--orange-300-main, #eb4c10);
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 128.571% */
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
  .ticket-icon__analysis {
    background: url('@manseImages/ticket-icon.svg') center center no-repeat;
    background-size: contain;
    width: 20px;
    height: 20px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
  }

  .overview-payment-inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    align-self: stretch;
  }
}
</style>
